import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field } from "formik";
import {
  Grid,
  Icon,
  IconButton,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { AppState } from "../../../store";
import {
  addNewBalanceRequest,
  fillBalanceField,
  removeBalanceRequest,
} from "../store/agent-form.slice";
import { fetchAllCurrencies } from "../../flights/store/currencies-list.slice";
import { ROLES } from "app/constants/roles";
import useAuthority from "app/hooks/useAuthority";

interface CreditsProps {
  errors?: any;
  touched?: any;
}

const Credits = ({
  errors = {},
  touched = {},
}: CreditsProps) => {
  
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { hasUserAnyAuthority } = useAuthority();

  const { currencies } = useSelector(
    (state: AppState) => state.flights.currenciesList
  );

  const agentForm = useSelector(
    (state: AppState) => state.agents.agentForm
  );

  const isAdmin = hasUserAnyAuthority([ROLES.ROLE_ADMIN]);

  const getDefaultCurrency = () => {
    const defaultCurrency = currencies?.find(
      (item) => item.isoCode === "EUR"
    );

    return defaultCurrency ? defaultCurrency.id : "";
  };

  useEffect(() => {
    dispatch(fetchAllCurrencies());
  }, [dispatch]);

  useEffect(() => {
    if (!currencies?.length) return;

    const first = agentForm.balanceRequests?.[0];

    if (!first) return;

    if (first.currencyId === "") {
      dispatch(
        fillBalanceField({
          id: first.id,
          field: "currencyId",
          value: getDefaultCurrency(),
        })
      );
    }
  }, [currencies, agentForm.balanceRequests, dispatch]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id?: string
  ) => {
    if (!isAdmin) return;

    dispatch(
      fillBalanceField({
        value: e.target.value,
        field: e.target.name,
        id,
      })
    );
  };

  const preventInvalidNumberInput = (e: any) => {
    if (
      e.key === "-" ||
      e.key === "e" ||
      e.key === "E" ||
      e.code === "Minus"
    ) {
      e.preventDefault();
    }
  };

  const getFieldError = (
    index: number,
    field: string
  ) => {
    return (
      touched?.balanceRequests?.[index]?.[field] &&
      errors?.balanceRequests?.[index]?.[field]
    );
  };

  return (
    <div style={{ margin: "0px 0px" }}>
      {agentForm?.balanceRequests?.map((balance, index) => (
        <Grid container spacing={4} key={balance.id}>
          <Grid item xs={3}>
            <Field
              as={TextField}
              style={{ width: "100%", marginRight: "20px" }}
              label={t(
                "app.main.agency.create.credit.input1",
                "Maximum credit"
              )}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange(e, balance.id)
              }
              variant="outlined"
              size="small"
              name="creditLimit"
              value={balance.creditLimit}
              type="number"
              disabled={!isAdmin}
              InputProps={{ inputProps: { min: 0 } }}
              onKeyDown={preventInvalidNumberInput}
              required
              error={Boolean(
                getFieldError(index, "creditLimit")
              )}
              helperText={
                getFieldError(index, "creditLimit")
              }
            />
          </Grid>

          <Grid item xs={3}>
            <div style={{ display: "flex" }}>
              <Field
                as={TextField}
                onChange={(
                  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                ) => handleChange(e, balance.id)}
                variant="outlined"
                value={balance.currencyId}
                name="currencyId"
                label={t(
                  "app.main.report.search.currency",
                  "Currency"
                )}
                select
                style={{ flex: 1, minWidth: 120 }}
                size="small"
                disabled={!isAdmin}
                required
                error={Boolean(
                  getFieldError(index, "currencyId")
                )}
                helperText={
                  getFieldError(index, "currencyId")
                }
              >
                {currencies &&
                  currencies
                    .filter((cr) =>
                      ["EUR", "CHF", "USD"].includes(cr.isoCode)
                    )
                    .map((cr: any, idx) => (
                      <MenuItem key={idx} value={cr.id}>
                        {cr.name} - {cr.isoCode}
                      </MenuItem>
                    ))}
              </Field>

              <IconButton
                style={{ margin: "0 10px" }}
                onClick={() => {
                  if (!isAdmin) return;

                  dispatch(
                    addNewBalanceRequest(getDefaultCurrency())
                  );
                }}
                size="small"
                disabled={!isAdmin}
              >
                <Icon>add</Icon>
              </IconButton>

              <IconButton
                onClick={() => {
                  if (!isAdmin) return;

                  dispatch(removeBalanceRequest(balance.id));
                }}
                size="small"
                disabled={
                  !isAdmin ||
                  agentForm.balanceRequests.length === 1
                }
              >
                <Icon color="error">delete</Icon>
              </IconButton>
            </div>
          </Grid>
        </Grid>
      ))}
    </div>
  );
};

export default Credits;